import styled, { css } from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getToken } from '@optimus-web/common';
import { ANIMATION_CURVE, ANIMATION_DURATION } from '@optimus-web/local-tokens';
import {
    getBackgroundColor,
    getBackgroundColorActive,
    getBackgroundColorHover,
    getBorderColor,
    getBorderColorActive,
    getBorderColorHover,
} from './getRadioStyles';

export interface RadioElementProps {
    inputHidden?: boolean;
    $disabled?: boolean;
}

export const RadioElement = styled.div<RadioElementProps>`
    flex-shrink: 0;
    display: inline-block;
    position: relative;
    width: ${getToken(TokenName.SIZING_200)}px;
    height: ${getToken(TokenName.SIZING_200)}px;
    margin-right: ${getToken(TokenName.SPACING_150)}px;
    ${({ inputHidden }) => inputHidden && 'display: none;'}
`;

export const RadioBackgroundElement = styled.div`
    display: inline-block;
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
`;

export interface RadioCircleProps {
    checked?: boolean;
    disabled?: boolean;
}

export const RadioOuterCircleElement = styled.div<RadioCircleProps>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-width: ${getToken(TokenName.BORDER_WIDTH_150)}px;
    border-style: solid;
    border-radius: ${getToken(TokenName.BORDER_RADIUS_ROUND)};
    transition: border-color 120ms ${ANIMATION_CURVE.default};
    border-color: ${(props) => getBorderColor(props)};
    background-color: ${(props) => getBackgroundColor(props)};

    &:hover {
        border-color: ${(props) => getBorderColorHover(props)};
        background-color: ${(props) => getBackgroundColorHover(props)};
    }

    &:active {
        border-color: ${(props) => getBorderColorActive(props)};
        background-color: ${(props) => getBackgroundColorActive(props)};
    }
`;

export const RadioInnerCircleElement = styled.div<RadioCircleProps>`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: ${getToken(TokenName.BORDER_RADIUS_ROUND)};
    background-color: ${getToken(TokenName.BACKGROUND_STATIC_FLAT)};
    transform: scale(0, 0);
    transition:
        transform ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default},
        background-color ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};

    ${({ disabled, checked }) =>
        disabled &&
        checked &&
        css`
            border-color: ${getToken(TokenName.BORDER_DISABLED)};
        `}

    ${({ checked }) =>
        checked &&
        css`
            transform: scale(0.4);
        `}
`;

export interface RadioInputElementProps {
    onChange: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    checked?: boolean;
    disabled?: boolean;
}

export const RadioInputElement = styled.input<RadioInputElementProps>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    opacity: 0;
    z-index: 1;
    pointer-events: none;

    &:focus-visible + ${RadioBackgroundElement} > ${RadioOuterCircleElement} {
        box-shadow: ${getToken(TokenName.FOCUS_OFFSET)};
        border-radius: ${getToken(TokenName.BORDER_RADIUS_ROUND)};
        outline: none;
    }
`;
